/**
 * GradientHatchDialog — the GRADIENT dialog (Faz 7.6, OCS gradient_editor
 * parity): 5-kind pattern grid + inverted (the 9 user patterns), 1/2 color
 * mode with tint, angle and centered, with a live strip preview. Applying
 * commits the pending boundary as a gradient hatch entity.
 */

import {
    Box,
    Button,
    Checkbox,
    Flex,
    Grid,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    SimpleGrid,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Stack,
    Text,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { create } from 'zustand';
import { useTranslation } from 'react-i18next';
import { makeEntity, undoableAdd } from '../../core/commands/entityOps';
import type { Pt3 } from '../../core/commands/geometryMath';
import { gradientPreviewUrl, type GradientData, type GradientKind } from '../../rendering/gradientFill';

interface GradientHatchDialogState {
    open: boolean;
    /** Closed boundary ring captured by the GRADIENT runner. */
    boundary: Pt3[] | null;
    start: (boundary: Pt3[]) => void;
    close: () => void;
}

export const useGradientHatchDialogStore = create<GradientHatchDialogState>((set) => ({
    open: false,
    boundary: null,
    start: (boundary) => set({ open: true, boundary }),
    close: () => set({ open: false, boundary: null }),
}));

const KINDS: GradientKind[] = ['linear', 'cylindrical', 'spherical', 'hemispherical', 'curved'];

/** Typed i18n keys per kind (template literals miss the key union). */
const KIND_LABEL_KEYS = {
    linear: 'ui.cad.gradient.kind_linear',
    cylindrical: 'ui.cad.gradient.kind_cylindrical',
    spherical: 'ui.cad.gradient.kind_spherical',
    hemispherical: 'ui.cad.gradient.kind_hemispherical',
    curved: 'ui.cad.gradient.kind_curved',
} as const;

export function GradientHatchDialog() {
    const { t } = useTranslation();
    const open = useGradientHatchDialogStore((s) => s.open);
    const boundary = useGradientHatchDialogStore((s) => s.boundary);
    const close = useGradientHatchDialogStore((s) => s.close);

    const [kind, setKind] = useState<GradientKind>('linear');
    const [inverted, setInverted] = useState(false);
    const [singleColor, setSingleColor] = useState(true);
    const [color1, setColor1] = useState('#2b6cb0');
    const [color2, setColor2] = useState('#f6ad55');
    const [tint, setTint] = useState(50);
    const [angleDeg, setAngleDeg] = useState(30);
    const [centered, setCentered] = useState(true);

    const draft: GradientData = useMemo(() => ({
        kind, inverted, color1, color2, singleColor,
        tint: tint / 100, angle: (angleDeg * Math.PI) / 180, centered,
    }), [kind, inverted, color1, color2, singleColor, tint, angleDeg, centered]);

    const preview = useMemo(() => gradientPreviewUrl(draft), [draft]);

    const apply = () => {
        if (!boundary || boundary.length < 3) { close(); return; }
        undoableAdd([makeEntity('hatch', `Gradient-${Date.now()}`, {
            boundary: boundary.map((p) => [p[0], p[1], p[2] ?? 0]),
            pattern: 'SOLID',
            fill: false,
            gradient: draft,
        })]);
        close();
    };

    return (
        <Modal isOpen={open} onClose={close} size="md">
            <ModalOverlay />
            <ModalContent bg="bg.panel">
                <ModalHeader fontSize="sm">{t('ui.cad.gradient.title')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Stack spacing={4}>
                        <Box>
                            <Text fontSize="10px" color="text.secondary" textTransform="uppercase" mb={1}>
                                {t('ui.cad.gradient.pattern')}
                            </Text>
                            <SimpleGrid columns={3} spacing={2}>
                                {KINDS.map((k) => {
                                    const thumb: GradientData = { ...draft, kind: k, inverted: false };
                                    return (
                                        <Box
                                            key={k}
                                            as="button"
                                            onClick={() => setKind(k)}
                                            border="1px solid"
                                            borderColor={kind === k ? 'brand.500' : 'border.base'}
                                            borderRadius="4px"
                                            p={1}
                                            bg={kind === k ? 'bg.active' : 'transparent'}
                                            _hover={{ bg: 'bg.hover' }}
                                        >
                                            {/* Inline style = runtime preview URL (allowed). */}
                                            <Box
                                                h="28px"
                                                borderRadius="2px"
                                                style={{ backgroundImage: `url(${gradientPreviewUrl(thumb)})` }}
                                            />
                                            <Text fontSize="10px" color="text.primary" noOfLines={1}>
                                                {t(KIND_LABEL_KEYS[k])}
                                            </Text>
                                        </Box>
                                    );
                                })}
                            </SimpleGrid>
                            <Checkbox
                                size="sm"
                                colorScheme="blue"
                                mt={2}
                                isChecked={inverted}
                                onChange={(e) => setInverted(e.target.checked)}
                            >
                                <Text fontSize="xs" color="text.primary">{t('ui.cad.gradient.inverted')}</Text>
                            </Checkbox>
                        </Box>

                        <RadioGroup
                            value={singleColor ? 'one' : 'two'}
                            onChange={(v) => setSingleColor(v === 'one')}
                        >
                            <Flex gap={4}>
                                <Radio value="one" size="sm" colorScheme="blue">
                                    <Text fontSize="xs" color="text.primary">{t('ui.cad.gradient.oneColor')}</Text>
                                </Radio>
                                <Radio value="two" size="sm" colorScheme="blue">
                                    <Text fontSize="xs" color="text.primary">{t('ui.cad.gradient.twoColors')}</Text>
                                </Radio>
                            </Flex>
                        </RadioGroup>

                        <Grid templateColumns="1fr 1fr" gap={2}>
                            <Flex align="center" gap={2}>
                                <Text fontSize="xs" color="text.primary" flexShrink={0}>{t('ui.cad.gradient.color1')}</Text>
                                <Input
                                    size="sm" type="color" p={0} h="28px" w="100%"
                                    value={color1}
                                    onChange={(e) => setColor1(e.target.value)}
                                />
                            </Flex>
                            <Flex align="center" gap={2} opacity={singleColor ? 0.4 : 1}>
                                <Text fontSize="xs" color="text.primary" flexShrink={0}>{t('ui.cad.gradient.color2')}</Text>
                                <Input
                                    size="sm" type="color" p={0} h="28px" w="100%"
                                    value={color2}
                                    isDisabled={singleColor}
                                    onChange={(e) => setColor2(e.target.value)}
                                />
                            </Flex>
                        </Grid>

                        <Box>
                            <Text fontSize="xs" color="text.primary" mb={1}>
                                {t('ui.cad.gradient.tint')}: {tint}%
                            </Text>
                            <Slider
                                size="sm"
                                min={0} max={100} step={1}
                                value={tint}
                                isDisabled={!singleColor}
                                onChange={(v) => setTint(Array.isArray(v) ? v[0] : v)}
                            >
                                <SliderTrack><SliderFilledTrack /></SliderTrack>
                                <SliderThumb />
                            </Slider>
                        </Box>

                        <Flex gap={4} align="center">
                            <Flex align="center" gap={2} minW={0}>
                                <Text fontSize="xs" color="text.primary" flexShrink={0}>{t('ui.cad.gradient.angle')}</Text>
                                <Input
                                    size="sm" variant="filled" bg="surface.300" color="text.primary"
                                    type="number" w="72px" flexShrink={0}
                                    value={angleDeg}
                                    onChange={(e) => setAngleDeg(Number(e.target.value) || 0)}
                                />
                            </Flex>
                            <Checkbox
                                size="sm" colorScheme="blue"
                                isChecked={centered}
                                onChange={(e) => setCentered(e.target.checked)}
                            >
                                <Text fontSize="xs" color="text.primary">{t('ui.cad.gradient.centered')}</Text>
                            </Checkbox>
                        </Flex>

                        {/* Live preview of the current draft (runtime URL). */}
                        <Box h="40px" borderRadius="4px" border="1px solid" borderColor="border.base"
                            style={{ backgroundImage: `url(${preview})`, backgroundSize: '100% 100%' }} />
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button size="sm" variant="ghost" mr={2} onClick={close}>
                        {t('common.cancel')}
                    </Button>
                    <Button size="sm" colorScheme="blue" onClick={apply} isDisabled={!boundary}>
                        {t('ui.cad.gradient.apply')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
